import { func, instanceOf, string } from "prop-types";
import React from "react";
import { Loader } from "semantic-ui-react";
import Pool from "./Pool";

export default class ImagePreloader extends React.PureComponent {
  static propTypes = {
    imageURL: string.isRequired,
    children: func.isRequired,
    // prefetchImageURLs is used in handleImageLoaded but eslint doesn't recognize it.
    // eslint-disable-next-line react/no-unused-prop-types
    prefetchImageURLs: instanceOf(Set)
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      imageURL: null
    };
    this.preLoaded = new Set();
    this.preLoading = new Set();
    this.timer = null;
  }

  componentDidMount() {
    const { imageURL } = this.props;
    this.preloadImage(imageURL);
  }

  componentDidUpdate(prevProps) {
    const { imageURL, prefetchImageURLs } = this.props;
    const {
      imageURL: prevImageUrl,
      prefetchImageURLs: prevPrefetchImageURLs
    } = prevProps;

    if (
      imageURL !== prevImageUrl ||
      prefetchImageURLs !== prevPrefetchImageURLs
    ) {
      this.updateImage(imageURL);
      this.updateLoading();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  updateImage = imageURL => {
    const { imageURL: currentImageURL } = this.state;

    if (this.preLoaded.has(imageURL)) {
      if (currentImageURL !== imageURL) {
        this.setState({ imageURL, loading: false });
      }
      this.startPrefetching();
    } else if (!this.preLoading.has(imageURL)) {
      this.preloadImage(imageURL);
    }
  };

  updateLoading = () => {
    const { loading } = this.state;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!loading) {
      // After a short delay, check if image is already loaded, otherwise display loader.
      // The delay is needed to give the browser cache some time to load from disk.
      this.timer = setTimeout(this.checkLoading, 100);
    }
  };

  checkLoading = () => {
    const { imageURL } = this.props;
    const { imageURL: currentImageURL, loading } = this.state;
    if (!loading && !(imageURL === currentImageURL))
      this.setState({ loading: true });
  };

  preloadImage = imageURL => {
    return new Promise(resolve => {
      if (this.preLoading.has(imageURL)) {
        resolve();
      } else {
        this.preLoading.add(imageURL);

        // Bind an event listener on the load to call the `resolve` function
        // Create a new image from JavaScript
        const image = new Image();

        image.onload = resolve;

        // If the image fails to be downloaded, we don't want the whole system
        // to collapse so we `resolve` instead of `reject`, even on error
        image.onerror = resolve;

        // Apply the path as `src` to the image so that the browser fetches it
        image.src = imageURL;
      }
    })
      .then(() => this.handleImageLoaded(imageURL))
      .catch(() => {
        this.preLoading.delete(imageURL);
      });
  };

  handleImageLoaded = loadedUrl => {
    if (this.unmounted) return;

    const { imageURL } = this.props;
    this.preLoaded.add(loadedUrl);

    const isMainImageLoaded = imageURL === loadedUrl;
    if (isMainImageLoaded) {
      this.updateImage(imageURL);
    }
  };

  startPrefetching() {
    const { prefetchImageURLs } = this.props;

    const pool = new Pool(5, url => {
      if (!this.preLoaded.has(url) && !this.preLoading.has(url)) {
        return this.preloadImage(url);
      }
      return Promise.resolve();
    });

    prefetchImageURLs.forEach(url => pool.schedule(url));
  }

  render() {
    const { children } = this.props;
    const { imageURL, loading } = this.state;

    return (
      <div data-component="remoteImage">
        <Loader size="huge" active={loading} />
        {children(imageURL)}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          className="placeholder"
        />
      </div>
    );
  }
}
